'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { Ticker } from '@benzinga/session';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { NumberFormatValues, NumericFormat } from 'react-number-format';
import { Icon, SectionSubtitle, SectionTitle } from '@benzinga/core-ui';
import { InputContainer, InputWrapper } from '../InputContainer';
import { Slider, Tooltip } from '@benzinga/core-ui';
import { SessionContext } from '@benzinga/session-context';
import { QuotesManager } from '@benzinga/quotes-manager';

export interface MarginCalculatorProps {
  ticker: Ticker | null;
}

type StockSale = { price: number; withCash: number; withMargin: number };

type StockSales = {
  stockSalePrice1: StockSale;
  stockSalePrice2: StockSale;
  stockSalePrice3: StockSale;
};

type StockSalePrices = {
  stockSalePrice1: number;
  stockSalePrice2: number;
  stockSalePrice3: number;
};

const stockSalePriceInitial: StockSales = {
  stockSalePrice1: {
    price: 0,
    withCash: 0,
    withMargin: 0,
  },
  stockSalePrice2: {
    price: 0,
    withCash: 0,
    withMargin: 0,
  },
  stockSalePrice3: {
    price: 0,
    withCash: 0,
    withMargin: 0,
  },
};

const calculateAbleToBorrow = (cash: number, marginPercent: number) => {
  const margin = Number(marginPercent) / 100;
  return Number((cash / margin - cash).toFixed(2));
};

const calculateSharesAbleToPurchase = (cash: number, stockPurchasePrice: number) => {
  return Number((cash / stockPurchasePrice).toFixed(2));
};

const calculateStockSaleMargin = (price: number, numberOfShares: number, stockPurchasePrice: number) => {
  const calculated = price * numberOfShares - stockPurchasePrice * numberOfShares;
  const result = Number(calculated.toFixed(2));
  return result;
};

const calculateStockSaleCash = (price: number, cash: number, stockPurchasePrice: number) => {
  const calculated =
    (Number(cash) / stockPurchasePrice) * price - (Number(cash) / stockPurchasePrice) * stockPurchasePrice;
  const result = Number(calculated.toFixed(2));
  return result;
};

export const MarginCalculator: React.FC<MarginCalculatorProps> = ({ ticker }) => {
  const [numberOfShares, setNumberOfShares] = React.useState(0);
  const [stockPurchasePrice, setStockPurchasePrice] = React.useState(0);
  const [cash, setCash] = React.useState<number>(10000);
  const [marginPercent, setMarginPercent] = React.useState<number>(30);

  const [amountAbleToBorrow, setAmountAbleToBorrow] = React.useState(0);
  const [sharesAbleToPurchase, setSharesAbleToPurchase] = React.useState(0);
  const [sharesAbleToPurchaseOnMargin, setSharesAbleToPurchaseOnMargin] = React.useState(0);

  const [stockSalePrices, setStockSalePrices] = React.useState(stockSalePriceInitial);

  const session = React.useContext(SessionContext);
  const quotesManager = session.getManager(QuotesManager);

  const [inputType, setInputType] = React.useState<'Cash' | 'Shares'>('Cash');
  const isInputTypeCash = inputType === 'Cash';
  const isInputTypeShares = inputType === 'Shares';

  const getNumberOfShares = React.useCallback((cash: number, marginPercent: number, stockPurchasePrice: number) => {
    const margin = Number(marginPercent) / 100;
    const result = Number((Number(cash) / margin / stockPurchasePrice).toFixed(2));
    return result;
  }, []);

  const getCash = React.useCallback((marginPercent: number, stockPurchasePrice: number, numberOfShares: number) => {
    const margin = Number(marginPercent) / 100;
    const cashRequirement = Number(numberOfShares) * stockPurchasePrice * margin;
    const result = Number(cashRequirement.toFixed(2));
    return result;
  }, []);

  const getStockSalePriceMultiplier = React.useCallback((price: number) => {
    let multiplier = 0.25;
    if (price <= 10) multiplier = 0.1;
    else if (price <= 50) multiplier = 0.25;
    else if (price <= 100) multiplier = 0.5;
    else if (price <= 250) multiplier = 1;
    else if (price <= 1000) multiplier = 5;
    else if (price <= 2500) multiplier = 10;
    else if (price > 2500) multiplier = 100;

    return multiplier;
  }, []);

  const getInitialStockSalePrices = React.useCallback((newStockPurchasePrice: number): StockSalePrices => {
    const numberOfZeros = Math.ceil(newStockPurchasePrice).toString().length - 1;
    const numberPower = Math.pow(10, numberOfZeros);
    const roundedStockPrice = Number(((newStockPurchasePrice / numberPower) * numberPower).toFixed(2));

    const result = {
      stockSalePrice1: roundedStockPrice * 0.9,
      stockSalePrice2: roundedStockPrice * 1.1,
      stockSalePrice3: roundedStockPrice * 1.25,
    };

    return result;
  }, []);

  const calculate = React.useCallback(
    (newValue: { cash?: number; shares?: number; stockPurchasePrice?: number; marginPercent?: number }) => {
      if (!ticker) return;

      const nextCash = newValue.cash ?? cash;
      const nextNumberOfShares = newValue.shares ?? numberOfShares;
      const nextMarginPercent = newValue.marginPercent ?? marginPercent;
      const nextStockPurchasePrice = newValue.stockPurchasePrice ?? stockPurchasePrice;

      const stockSalePricesArray = Object.values(stockSalePrices).map(item => {
        const withMargin = calculateStockSaleMargin(item.price, nextNumberOfShares, nextStockPurchasePrice);
        const withCash = calculateStockSaleCash(item.price, nextCash, nextStockPurchasePrice);

        const newItem = {
          price: item.price,
          withCash,
          withMargin,
        };
        return newItem;
      });

      const newStockSalePrices = {
        stockSalePrice1: stockSalePricesArray[0],
        stockSalePrice2: stockSalePricesArray[1],
        stockSalePrice3: stockSalePricesArray[2],
      };

      const amountAbleToBorrow = calculateAbleToBorrow(nextCash, nextMarginPercent);
      const sharesAbleToPurchase = calculateSharesAbleToPurchase(cash + amountAbleToBorrow, nextStockPurchasePrice);
      const sharesAbleToPurchaseOnMargin = calculateSharesAbleToPurchase(amountAbleToBorrow, nextStockPurchasePrice);

      setAmountAbleToBorrow(amountAbleToBorrow);
      setSharesAbleToPurchase(sharesAbleToPurchase);
      setSharesAbleToPurchaseOnMargin(sharesAbleToPurchaseOnMargin);

      setStockSalePrices(newStockSalePrices);
    },
    [
      cash,
      marginPercent,
      numberOfShares,
      stockPurchasePrice,
      stockSalePrices,
      ticker,
      setAmountAbleToBorrow,
      setSharesAbleToPurchase,
      setSharesAbleToPurchaseOnMargin,
    ],
  );

  // Hooks.useSubscriber(userManager, (event: UserManagerEvent) => {
  //   if (event.type === 'user:user_update') {
  //     loadWatchlists();
  //   }
  // });

  React.useEffect(() => {
    if (!ticker?.symbol) return;
    const calculateInitialStockSalePrices = (newStockPurchasePrice: number, shares: number, cash: number) => {
      const result = getInitialStockSalePrices(newStockPurchasePrice);

      const withMargin = (price: number) => calculateStockSaleMargin(Number(price), shares, newStockPurchasePrice);
      const withCash = (price: number) => calculateStockSaleCash(Number(price), cash, newStockPurchasePrice);

      const newStockSalePrices = {
        stockSalePrice1: {
          price: result.stockSalePrice1,
          withCash: withCash(result.stockSalePrice1),
          withMargin: withMargin(result.stockSalePrice1),
        },
        stockSalePrice2: {
          price: result.stockSalePrice2,
          withCash: withCash(result.stockSalePrice2),
          withMargin: withMargin(result.stockSalePrice2),
        },
        stockSalePrice3: {
          price: result.stockSalePrice3,
          withCash: withCash(result.stockSalePrice3),
          withMargin: withMargin(result.stockSalePrice3),
        },
      };

      setStockSalePrices(newStockSalePrices);
    };

    quotesManager.getDelayedQuotes([ticker.symbol]).then(res => {
      if (res?.ok) {
        const quote = res?.ok[ticker.symbol];
        if (quote && quote.lastTradePrice) {
          const nextStockPurchasePrice = quote.lastTradePrice;
          setStockPurchasePrice(nextStockPurchasePrice);

          const margin = Number(marginPercent) / 100;
          const shares = Number((Number(cash) / margin / nextStockPurchasePrice).toFixed(2));
          setNumberOfShares(shares);

          calculate({ cash, marginPercent: Number(marginPercent), shares, stockPurchasePrice: nextStockPurchasePrice });
          calculateInitialStockSalePrices(nextStockPurchasePrice, shares, cash);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker?.symbol]);

  const handleOnChangeCash = React.useCallback(
    (value: number) => {
      if (isInputTypeShares) return;
      if (value) {
        const shares = getNumberOfShares(value, Number(marginPercent), stockPurchasePrice);
        setNumberOfShares(shares);
        calculate({ cash: value, shares });
        setCash(value);
      }
    },
    [calculate, getNumberOfShares, isInputTypeShares, marginPercent, stockPurchasePrice],
  );

  const handleOnChangeStockPurchasePrice = React.useCallback(
    (value: number) => {
      if (value) {
        calculate({ stockPurchasePrice: value });
        setStockPurchasePrice(value);
      }
    },
    [calculate],
  );

  const handleOnChangeMargin = React.useCallback(
    (value: number) => {
      if (value) {
        const shares = getNumberOfShares(cash, value, stockPurchasePrice);
        setNumberOfShares(shares);
        calculate({ marginPercent: value, shares });
        setMarginPercent(value);
      }
    },
    [calculate, cash, getNumberOfShares, stockPurchasePrice],
  );

  const handleOnChangeNumberOfShares = React.useCallback(
    (value: number) => {
      if (isInputTypeCash) return;

      if (value) {
        const nextCash = getCash(marginPercent, stockPurchasePrice, value);
        setCash(nextCash);
        calculate({ cash: nextCash, shares: value });
        setNumberOfShares(value);
      }
    },
    [calculate, getCash, isInputTypeCash, marginPercent, stockPurchasePrice],
  );

  const handleChangeType = (value: 'Cash' | 'Shares') => setInputType(value);

  const handleOnChangeSalePrice = React.useCallback(
    (value: number, name: string) => {
      if (value && name) {
        const newStockSalePrices = {
          stockSalePrice1: stockSalePrices.stockSalePrice1,
          stockSalePrice2: stockSalePrices.stockSalePrice2,
          stockSalePrice3: stockSalePrices.stockSalePrice3,
        };

        const margin = Number(marginPercent) / 100;
        const nextNumberOfShares = Number((Number(cash) / margin / stockPurchasePrice).toFixed(2));

        const withMargin = calculateStockSaleMargin(value, nextNumberOfShares, stockPurchasePrice);
        const withCash = calculateStockSaleCash(value, cash, stockPurchasePrice);

        const newStockSalePrice = {
          price: value,
          withCash,
          withMargin,
        };

        if (name === 'Stock Sale Price 1') {
          newStockSalePrices.stockSalePrice1 = newStockSalePrice;
        } else if (name === 'Stock Sale Price 2') {
          newStockSalePrices.stockSalePrice2 = newStockSalePrice;
        } else if (name === 'Stock Sale Price 3') {
          newStockSalePrices.stockSalePrice3 = newStockSalePrice;
        } else return;

        setStockSalePrices(newStockSalePrices);
      }
    },
    [
      cash,
      marginPercent,
      stockPurchasePrice,
      stockSalePrices.stockSalePrice1,
      stockSalePrices.stockSalePrice2,
      stockSalePrices.stockSalePrice3,
    ],
  );

  const getClassName = (price: number): string => {
    let className = '';
    if (price > 0) return (className = 'gains');
    if (price < 0) return (className = 'losses');
    return className;
  };

  const MAX_VAL = 99;
  const withValueCap = (inputObj: NumberFormatValues): boolean => {
    const { value } = inputObj;
    if (Number(value) <= MAX_VAL) return true;
    return false;
  };

  const sliderTipFormatter = (value?: number): React.ReactNode => {
    if (!value) return null;
    const result: number = ((value - stockPurchasePrice) / stockPurchasePrice) * 100;
    return `${result.toFixed(1)}%`;
  };

  const MarginTooltip = (
    <div>
      The percentage of equity/cash required when an investor opens a position.{' '}
      <span style={{ fontWeight: 700 }}>Max: 99%</span>
    </div>
  );

  return (
    <MarginCalculatorContainer className="margin-calculator-container">
      <div>
        <div className="inputs-container">
          <div className="calculations-top">
            <div>
              <InputWrapper name="Calculate By Cash or Shares">
                <div className="input-type-container">
                  <button className={`${isInputTypeCash ? 'active' : ''}`} onClick={() => handleChangeType('Cash')}>
                    Cash
                  </button>
                  <button className={`${isInputTypeShares ? 'active' : ''}`} onClick={() => handleChangeType('Shares')}>
                    Shares
                  </button>
                </div>
              </InputWrapper>
              {isInputTypeCash ? (
                <InputContainer
                  disabled={isInputTypeShares}
                  name={isInputTypeCash ? 'Cash to Invest' : 'Cash Requirement'}
                  onChange={handleOnChangeCash}
                  prefix="$"
                  tooltip="Amount of cash out of pocket to invest"
                  value={cash}
                />
              ) : (
                <InputContainer
                  disabled={isInputTypeCash}
                  name={isInputTypeCash ? 'Number of Shares' : 'Number of Shares to Purchase'}
                  onChange={handleOnChangeNumberOfShares}
                  tooltip="Number of shares of stock able to be purchased"
                  value={numberOfShares}
                />
              )}
            </div>
            <div>
              <InputContainer
                name="Stock Purchase Price"
                onChange={handleOnChangeStockPurchasePrice}
                prefix="$"
                tooltip="The current market price of the stock you wish to purchase"
                value={stockPurchasePrice}
              />
              <InputContainer
                isAllowed={withValueCap}
                name="Margin %"
                onChange={handleOnChangeMargin}
                suffix="%"
                tooltip={MarginTooltip}
                value={marginPercent}
              />
            </div>
          </div>
        </div>
        {ticker ? (
          <div className="margin-results-container">
            <div className="calculations-bottom">
              <div>
                <h5>
                  Amount Able To Borrow
                  <Tooltip content="Amount of money able to be borrowed based on the margin requirement" width={250}>
                    <Icon className="info-circle-icon" icon={faInfoCircle} />
                  </Tooltip>
                </h5>
                <NumericFormat
                  decimalScale={2}
                  displayType={'text'}
                  fixedDecimalScale={true}
                  prefix={'$'}
                  thousandSeparator={true}
                  value={amountAbleToBorrow}
                />
                <div className="total-summary">
                  <span>Total Purchase Value: </span>
                  <NumericFormat
                    decimalScale={2}
                    displayType={'text'}
                    fixedDecimalScale={true}
                    prefix={'$'}
                    thousandSeparator={true}
                    value={amountAbleToBorrow + cash}
                  />
                </div>
              </div>
              <div>
                <h5>
                  Shares Purchased on Margin
                  <Tooltip content="Number of shares able to be purchased with the leverage" width={250}>
                    <Icon className="info-circle-icon" icon={faInfoCircle} />
                  </Tooltip>
                </h5>
                <NumericFormat
                  decimalScale={2}
                  displayType={'text'}
                  fixedDecimalScale={true}
                  thousandSeparator={true}
                  value={sharesAbleToPurchaseOnMargin}
                />
                <div className="total-summary">
                  <span>Total Shares to Purchase: </span>
                  <NumericFormat
                    decimalScale={2}
                    displayType={'text'}
                    fixedDecimalScale={true}
                    thousandSeparator={true}
                    value={sharesAbleToPurchase}
                  />
                </div>
              </div>
            </div>
            <div className="stock-sale-prices-container">
              <div className="stock-sale-prices-list-container">
                <SectionTitle>Sale Price comparisons</SectionTitle>
                <SectionSubtitle>
                  Compare loss/profit on cash vs. margin purchases on {ticker?.name} ({ticker?.symbol})
                </SectionSubtitle>
              </div>
              <ul className="stock-sale-prices-cards-list">
                {Object.values(stockSalePrices).map((stockSale, index) => {
                  return (
                    <li key={index}>
                      <div className="stock-sale-price-card-header">
                        <h4>Stock Sale Price</h4>
                        <NumericFormat
                          className="stock-sale-price"
                          decimalScale={2}
                          displayType={'text'}
                          prefix={'$'}
                          thousandSeparator={true}
                          value={stockSale.price ? stockSale.price : '-'}
                        />
                      </div>
                      <div className="slider-wrapper">
                        <Slider
                          defaultValue={stockSale.price || 0}
                          max={stockPurchasePrice * 2}
                          min={stockPurchasePrice / 2}
                          onChange={(value: number) => handleOnChangeSalePrice(value, `Stock Sale Price ${index + 1}`)}
                          step={getStockSalePriceMultiplier(stockPurchasePrice)}
                          tipFormatter={sliderTipFormatter}
                        />
                      </div>
                      <div className="with-margin-block">
                        <h6>With Margin</h6>
                        <NumericFormat
                          className={stockSale.withMargin ? getClassName(stockSale.withMargin) : ''}
                          decimalScale={2}
                          displayType={'text'}
                          fixedDecimalScale={true}
                          prefix={'$'}
                          thousandSeparator={true}
                          value={stockSale.withMargin ? stockSale.withMargin : '-'}
                        />
                      </div>
                      <div className="with-cash-block">
                        <h6>Cash</h6>
                        <NumericFormat
                          className={stockSale.withCash ? getClassName(stockSale.withCash) : ''}
                          decimalScale={2}
                          displayType={'text'}
                          fixedDecimalScale={true}
                          prefix={'$'}
                          thousandSeparator={true}
                          value={stockSale.withCash ? stockSale.withCash : '-'}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        ) : null}
      </div>
    </MarginCalculatorContainer>
  );
};

const MarginCalculatorContainer = styled.div`
  &.margin-calculator-container {
    .inputs-container {
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    .input-box {
      margin-bottom: 10px;

      input {
        width: 100%;
      }
    }

    .tooltip-container {
      margin-left: 5px;
    }

    .info-circle-icon {
      font-size: ${({ theme }) => theme.fontSize.sm};
      width: 14px;
      height: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .margin-results-container {
      padding: 16px;
      margin-top: 16px;
      background-color: ${({ theme }) => theme.colors.backgroundActive};
      border: 1px solid ${({ theme }) => theme.colors.border};
      border-radius: 4px;
    }

    .total-summary {
      font-size: 12px;
      color: ${({ theme }) => theme.colors.foregroundInactive};
      margin-top: 2px !important;
      margin-bottom: 4px !important;
    }

    .stock-sale-prices-container {
    }

    .stock-sale-prices-cards-list {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      width: 100%;

      li {
        width: 100%;

        @media screen and (max-width: 950px) {
          &:not(:last-of-type) {
            display: none;
          }
        }

        &:last-of-type {
          margin-right: 0;
        }

        .stock-sale-price-card-header {
          h4 {
            color: ${({ theme }) => theme.colors.foreground};
            font-weight: ${({ theme }) => theme.fontWeight.semibold};
          }

          .stock-sale-price {
            color: ${({ theme }) => theme.colorPalette.blue600};
            font-weight: ${({ theme }) => theme.fontWeight.semibold};
            font-size: ${({ theme }) => theme.fontSize['5xl']};
            line-height: 1;
          }

          & ~ div {
            h6 {
              color: ${({ theme }) => theme.colors.foreground};
            }

            span {
              color: ${({ theme }) => theme.colors.foreground};
              font-size: ${({ theme }) => theme.fontSize['4xl']};

              &.gains {
                color: ${({ theme }) => theme.colorPalette.green500};
              }

              &.losses {
                color: ${({ theme }) => theme.colorPalette.red600};
              }
            }
          }
        }

        > div {
          .with-margin-block,
          .with-cash-block {
            height: 120px;
          }

          .with-margin-block {
            border-bottom: 1px solid rgba(203, 224, 242, 0.5);
          }
        }
      }
    }

    .inputs-container {
      width: 100%;

      .calculations-top {
        display: flex;
        flex-direction: column;

        > div {
          width: 100%;
          margin-right: 10px;
        }
      }

      .calculate-button {
        color: ${({ theme }) => theme.colorPalette.white};
        background-color: ${({ theme }) => theme.colorPalette.blue600};
        text-transform: uppercase;
        width: 250px;
        height: 60px;
        font-weight: ${({ theme }) => theme.fontWeight.semibold};
        border-radius: ${({ theme }) => theme.borderRadius.default};
        margin-bottom: 30px;
      }

      .input-type-container {
        height: 40px;
        display: flex;
        border-radius: ${({ theme }) => theme.borderRadius.default};
        overflow: hidden;
        box-sizing: border-box;

        button {
          width: 100%;
          height: 100%;
          color: ${({ theme }) => theme.colors.brandLight};
          background: ${({ theme }) => theme.colorPalette.white};
          overflow: hidden;
          border: 2px solid ${({ theme }) => theme.colors.border};
          border-radius: ${({ theme }) => theme.borderRadius.default};
          font-weight: ${({ theme }) => theme.fontWeight.semibold};

          &:nth-of-type(1) {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            border-right-width: 0;
          }

          &:nth-of-type(2) {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            border-left-width: 0;
          }

          &.active {
            color: ${({ theme }) => theme.colorPalette.white};
            background: ${({ theme }) => theme.colors.brand};
          }
        }
      }
    }

    .calculations-bottom {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
      padding-bottom: 8px;
      margin-bottom: 16px;

      > div {
        width: 100%;

        &:first-of-type {
          margin: 8px 0;
        }

        > span {
          font-size: ${({ theme }) => theme.fontSize['4xl']};
          color: ${({ theme }) => theme.colors.foreground};
        }
      }
    }

    h5 {
      text-transform: uppercase;
      color: #5b7292;
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
      display: flex;
      align-items: center;
    }

    h3 {
      color: ${({ theme }) => theme.colors.foreground};
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
      font-size: ${({ theme }) => theme.fontSize.xl};
      margin-bottom: 2px;
    }

    .stock-sale-prices-list-container {
      width: 100%;
      margin-left: 0;
      margin-bottom: 16px;

      .stock-sale-prices-list {
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        @media screen and (min-width: 600px) {
          flex-direction: row;
        }

        .stock-sale-price-wrapper {
          margin-right: 4px;
          &:last-of-type {
            margin-right: 0;
          }
        }
      }
    }

    @media (min-width: 950px) {
      .inputs-container {
        flex-direction: column;
        justify-content: space-between;
        max-width: 1400px;
        margin: 0 auto;
      }

      .stock-sale-prices-cards-list {
        flex-direction: row;
        margin-top: 0;

        li {
          margin-right: 24px;
        }
      }
    }

    @media (min-width: 550px) {
      .inputs-container {
        .calculations-top {
          flex-direction: row;
        }
      }

      .input-box {
        input {
          max-width: 300px;
        }
      }

      .input-type-container {
        width: 250px;
      }
    }
  }
`;
